import { Matrix4 } from "three";
import { existingPointCloudInViewer } from "../../../common/potreeUtils";
import { DataIndexItem } from "../../../types/interfaces";
import { EPSGInfo } from "../../../common/EPSGInfo";
import { getEPSGDetails } from "../../../common/gisUtils";

export const grouping = (dataIndexItems: DataIndexItem[]) => {
    const projects: string[] = [];
    dataIndexItems.forEach((dataIndexItem) => {
        if (dataIndexItem.project && !projects.includes(dataIndexItem.project)) {
            projects.push(dataIndexItem.project);
        }
    });

    let result: DataIndexItem[] = [];
    projects.forEach((project) => {
        const data: any = {
            data_id: project,
            id_iterator: "",
            batch_iterator: "",
            name: project,
            raw_epsg: "",
            raw_epoch: "",
            raw_geoid: "",
            acquisition_date: "",
            acquisition_type: "",
            conversion_status: "",
            raw_size: "0",
            entwine_build_path: "",
            center: "",
            radius: "",
            project: project,
            uuid: "",
            show: false,
            loaded: false,
            isProject: true,
            children: []
        };

        data.children = dataIndexItems.filter((item) => item.project === project);
        result.push(data);
    });

    const noProjectItems = dataIndexItems.filter((item) => item.project === "");
    result = [...result, ...noProjectItems];
    return { projects: projects, dataList: result };
};

export const getDataItemWithDataId = (dataIndexItems: DataIndexItem[], dataId: string) => {
    let dataIndexItem: DataIndexItem | undefined;
    dataIndexItems.forEach((item) => {
        if (dataIndexItem) return;
        if (item.isProject && item.children !== undefined) {
            dataIndexItem = item.children.find((c) => c.data_id === dataId);
        } else if (item.data_id === dataId) {
            dataIndexItem = { ...item };
        }
    });

    if (!dataIndexItem) {
        throw new Error(`Can not find item with the data id - ${dataId}`);
    }

    return dataIndexItem;
};

export const getDataItemsWithBuildPath = (dataIndexItems: DataIndexItem[], entwineBuildPath: string) => {
    const dataIndexItemList: DataIndexItem[] = [];
    dataIndexItems.forEach((item) => {
        let dataIndexItem: DataIndexItem | undefined;
        if (item.isProject && item.children !== undefined) {
            dataIndexItem = item.children.find((c) => c.entwine_build_path === entwineBuildPath);
        } else if (item.entwine_build_path === entwineBuildPath) {
            dataIndexItem = { ...item };
        }

        if (dataIndexItem) {
            dataIndexItemList.push(dataIndexItem);
        }
    });

    return dataIndexItemList;
};

export const getDataItemWithPointSourceIdAndBuildPath = (
    dataIndexItems: DataIndexItem[],
    pointSourceId: string,
    entwineBuildPath: string
) => {
    let dataIndexItem: DataIndexItem | undefined;
    dataIndexItems.forEach((item) => {
        if (item.isProject && item.children !== undefined) {
            dataIndexItem = item.children.find(
                (c) => c.id_iterator === pointSourceId && c.entwine_build_path === entwineBuildPath
            );
        } else if (item.id_iterator === pointSourceId && item.entwine_build_path === entwineBuildPath) {
            dataIndexItem = { ...item };
        }
    });

    if (!dataIndexItem) {
        throw new Error(`Can not find item with the pointSourceId - ${pointSourceId}`);
    }

    return dataIndexItem;
};

export const changeDataItem = (dataIndexItems: DataIndexItem[], dataId: string, newData: any) => {
    const temp = JSON.parse(JSON.stringify(dataIndexItems));
    let project = "";
    let projectIndex = -1;
    let indexNumber = -1;
    temp.forEach((item: DataIndexItem, itemIndex: number) => {
        if (item.project && item.children) {
            item.children.forEach((c: DataIndexItem, index: number) => {
                if (c.data_id === dataId) {
                    project = item.project;
                    projectIndex = itemIndex;
                    indexNumber = index;
                }
            });
        } else if (item.data_id === dataId) {
            indexNumber = itemIndex;
        }
    });

    if (indexNumber > -1) {
        if (project) {
            const children = temp[projectIndex].children;
            if (children) {
                children[indexNumber] = { ...children[indexNumber], ...newData };
            }
        } else {
            temp[indexNumber] = { ...temp[indexNumber], ...newData };
        }
    }

    return temp;
};

export const removeDataItem = (dataIndexItems: DataIndexItem[], dataId: string) => {
    const temp = JSON.parse(JSON.stringify(dataIndexItems)) as DataIndexItem[];
    let project = "";
    let indexNumber = -1;
    temp.forEach((item: DataIndexItem, itemIndex: number) => {
        if (item.project && item.children) {
            item.children.forEach((c: DataIndexItem, index: number) => {
                if (c.data_id === dataId) {
                    project = item.project;
                    indexNumber = index;
                }
            });
            if (indexNumber > -1) {
                item.children.splice(indexNumber, 1);
            }
        } else if (item.data_id === dataId) {
            indexNumber = itemIndex;
        }
    });

    if (!project && indexNumber > -1) {
        temp.splice(indexNumber, 1);
    }

    return temp;
};

export const resetOriginDataList = (dataList: DataIndexItem[], newValues: any) => {
    const temp = JSON.parse(JSON.stringify(dataList)) as DataIndexItem[];
    for (let i = 0; i < temp.length; i++) {
        const dataItem = temp[i];
        const dataId = dataItem.data_id;

        let loaded = false;
        let show = false;
        let uuid = "";
        const pointCloud = existingPointCloudInViewer(dataItem);
        if (pointCloud) {
            loaded = true;
            uuid = pointCloud.uuid;

            if (dataItem.id_iterator) {
                const mat4 = pointCloud.uCustomFilterPointSourceIDs as Matrix4;
                if (mat4.elements.includes(parseInt(dataItem.id_iterator, 10))) {
                    show = true;
                }
            }
        }

        temp[i] = { ...dataItem, uuid: uuid, show: show, loaded: loaded };
        if (dataId === newValues.data_id) {
            temp[i] = { ...temp[i], ...newValues, isProject: !!newValues.project };
        }
    }

    return temp;
};

export const getProjectionFromDataIndex = (record: any) => {
    const pointCloud = existingPointCloudInViewer(record);
    
    let epsgData = new EPSGInfo("", "");
    if (record.entwine_epsg) {
        epsgData.horizontal = record.entwine_epsg.split('+')[0]
        epsgData.vertical = record.entwine_epsg.split('+')[1]
    }
    else {
        epsgData = getEPSGDetails(pointCloud.pcoGeometry.info.srs.wkt);
    }
    return epsgData
}

export const failedStatus = ["FAILED upload", "FAILED processing", "FAILED build"]
export const convertStatus = ["converting", "converted", "building"]